// Чтение данных: клиент скачивает снапшот из R2 (через Worker) и хранит его
// в IndexedDB. Обновление — по смене версии в data/meta.json: при старте,
// раз в 10 минут, при возвращении сети/видимости приложения и по pull-to-refresh.

import type { Ad, SnapshotMeta, SnapshotChunk, Subscription } from '@barter/shared';
import {
  getCategoryIdsWithDescendants,
  resolveCategoryId,
} from '@barter/shared';
import { db, getSyncMeta, setSyncMeta, getPendingOps, getActiveSubscriptions } from '../db/database';
import { API_BASE } from './api-base';

export const SNAPSHOT_POLL_INTERVAL_MS = 10 * 60 * 1000;

const FETCH_TIMEOUT_MS = 30_000;
const FETCH_RETRIES = 3;
const RETRY_AFTER_FAILURE_MS = 30_000;
/** Одновременных загрузок чанков: параллельно быстрее, но слабой мобильной
 *  сети легче с небольшим числом соединений. */
const CHUNK_CONCURRENCY = 3;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * fetch с таймаутом и повторами. Без этого один зависший запрос (типично на
 * слабой мобильной сети) навсегда вешал inFlight-промис и вся синхронизация
 * останавливалась до перезагрузки страницы.
 */
let lastFailedUrl = '';

async function fetchJson<T>(path: string): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= FETCH_RETRIES; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Snapshot fetch failed: HTTP ${res.status} (${path})`);
      return (await res.json()) as T;
    } catch (err) {
      lastErr = err;
      lastFailedUrl = `${API_BASE}${path}`;
      if (attempt < FETCH_RETRIES) await sleep(1500 * (attempt + 1));
    } finally {
      clearTimeout(timer);
    }
  }
      throw lastErr;
}

function matchesSubscription(ad: Ad, subs: Subscription[]): boolean {
  return subs.some(
    (s) =>
      getCategoryIdsWithDescendants(resolveCategoryId(s.categoryId)).includes(
        resolveCategoryId(ad.categoryId),
      ) &&
      (!s.city || !ad.location?.city || s.city.toLowerCase() === ad.location.city.toLowerCase()),
  );
}

let inFlight: Promise<boolean> | null = null;

// ---------------------------------------------------------------------------
// Прогресс загрузки снапшота: баннер «Оновлюю каталог… k/n» и состояние
// «первой загрузки каталога» (база ещё пуста — списки показывают загрузку,
// а не «нічого не знайдено»).
// ---------------------------------------------------------------------------

export interface SnapshotProgress {
  active: boolean;
  loaded: number;
  total: number;
  /** true, когда локальная база была пуста на момент старта загрузки */
  firstLoad: boolean;
  /** последняя попытка загрузки завершилась ошибкой (баннер покажет это) */
  failed: boolean;
  /** последний неудачный адрес — сразу видно, что именно блокируется */
  failedUrl?: string;
}

let progress: SnapshotProgress = {
  active: false,
  loaded: 0,
  total: 0,
  firstLoad: false,
  failed: false,
};
const progressListeners = new Set<(p: SnapshotProgress) => void>();

function setProgress(next: Partial<SnapshotProgress>): void {
  progress = { ...progress, ...next };
  for (const cb of progressListeners) {
    try {
      cb(progress);
    } catch {
      /* подписчик упал — остальных это не касается */
    }
  }
}

export function getSnapshotProgress(): SnapshotProgress {
  return progress;
}

export function subscribeSnapshotProgress(cb: (p: SnapshotProgress) => void): () => void {
  progressListeners.add(cb);
  return () => progressListeners.delete(cb);
}

let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** Автоповтор после сбоя: сетевые неудачи типичны на телефонах, без повтора
 *  пользователь до следующего 10-минутного опроса видел бы вечное «Завантаження…». */
function scheduleRetryAfterFailure(): void {
  if (retryTimer) return;
  retryTimer = setTimeout(
    () => {
      retryTimer = null;
      refreshSnapshot();
    },
    RETRY_AFTER_FAILURE_MS,
  );
}

/**
 * Проверяет версию снапшота и при изменении перекачивает объявления в Dexie.
 * Возвращает true, если новая версия была применена.
 */
export function refreshSnapshot(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = doRefresh()
    .catch((err) => {
      console.warn('[Snapshot] refresh failed:', err);
      setProgress({ active: false, failed: true, failedUrl: lastFailedUrl || undefined });
      scheduleRetryAfterFailure();
      return false;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

async function doRefresh(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

  setProgress({ failed: false, failedUrl: undefined });
  lastFailedUrl = '';

  // На пустой базе включаем индикатор до запроса meta.json, иначе до его
  // ответа мигает «Поки немає оголошень».
  const adsCountBefore = await db.ads.count();
  const firstLoad = adsCountBefore === 0;
  if (firstLoad) setProgress({ active: true, loaded: 0, total: 0, firstLoad: true });

  const meta = await fetchJson<SnapshotMeta>('/data/meta.json');
  const currentVersion = await getSyncMeta('snapshotVersion');
  if (meta.version === currentVersion) {
    setProgress({ active: false });
    return false;
  }

  setProgress({ active: true, loaded: 0, total: meta.chunks.length, firstLoad });

  const chunks: SnapshotChunk[] = new Array(meta.chunks.length);
  let loaded = 0;
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(CHUNK_CONCURRENCY, meta.chunks.length) },
    async () => {
      while (cursor < meta.chunks.length) {
        const i = cursor++;
        chunks[i] = await fetchJson<SnapshotChunk>(`/data/${meta.chunks[i]}`);
        loaded++;
        setProgress({ loaded });
      }
    },
  );
  await Promise.all(workers);

  const beforeIds = new Set((await db.ads.toArray()).map((a) => a.id));
  // Локальные ещё не опубликованные объявления (есть pending create) не трогаем.
  const pendingCreateIds = new Set(
    (await getPendingOps()).filter((o) => o.type === 'create').map((o) => o.adId),
  );

  // Пишем по чанкам в отдельных транзакциях: чтения (поиск/главная) не
  // блокируются на всю запись, объявления появляются прогрессивно.
  for (const chunk of chunks) {
    await db.transaction('rw', db.ads, async () => {
      await db.ads.bulkPut(chunk.ads);
    });
  }

  const snapshotIds = new Set(chunks.flatMap((c) => c.ads.map((a) => a.id)));
  await db.transaction('rw', db.ads, async () => {
    const stale = await db.ads
      .filter((a) => !snapshotIds.has(a.id) && !pendingCreateIds.has(a.id))
      .primaryKeys();
    await db.ads.bulkDelete(stale);
  });

  await setSyncMeta('snapshotVersion', meta.version);
  await setSyncMeta('lastSnapshotAt', String(Date.now()));

  setProgress({ active: false, loaded: meta.chunks.length });

  window.dispatchEvent(new Event('barter:new-ads'));

  const ads = chunks.flatMap((c) => c.ads);
  const newMatched = ads.filter((a) => a.status === 'active' && !beforeIds.has(a.id));
  if (newMatched.length > 0) {
    const subs = await getActiveSubscriptions();
    if (subs.length > 0 && newMatched.some((a) => matchesSubscription(a, subs))) {
      window.dispatchEvent(
        new CustomEvent('barter:sub-match', { detail: { count: newMatched.length } }),
      );
    }
  }

  return true;
}

/** Запускается один раз из main.tsx: первая загрузка + периодический опрос. */
export function initSnapshotPolling(): void {
  refreshSnapshot();
  setInterval(() => refreshSnapshot(), SNAPSHOT_POLL_INTERVAL_MS);
  window.addEventListener('online', () => refreshSnapshot());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshSnapshot();
  });
}

/** Похожие объявления считаем локально (замена GET /api/search/similar). */
export async function findSimilarAds(ad: Ad, limit = 5): Promise<Ad[]> {
  const catIds = new Set(getCategoryIdsWithDescendants(resolveCategoryId(ad.categoryId)));
  const sameCategory = await db.ads
    .where('categoryId')
    .anyOf([...catIds])
    .filter(
      (a) => a.id !== ad.id && a.status === 'active' && catIds.has(resolveCategoryId(a.categoryId)),
    )
    .toArray();

  const tokens = (s: string) =>
    new Set(
      s
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .split(/\s+/)
        .filter((w) => w.length >= 3),
    );
  const adTokens = tokens(`${ad.title} ${ad.description} ${ad.wanted ?? ''}`);

  const scored = sameCategory.map((candidate) => {
    const candidateTokens = tokens(
      `${candidate.title} ${candidate.description} ${candidate.wanted ?? ''}`,
    );
    let overlap = 0;
    for (const tok of candidateTokens) if (adTokens.has(tok)) overlap++;
    return { ad: candidate, overlap };
  });

  scored.sort((a, b) => b.overlap - a.overlap || b.ad.createdAt - a.ad.createdAt);
  return scored.slice(0, limit).map((s) => s.ad);
}
